import { Component, OnDestroy, OnInit } from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { Invitation, NetworkPartner, Privilege, User } from '@kion/data-models';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CmsConfig } from '../../config/cms.config';
import { ToastService } from '@kion/ui';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { NetworkPartnersFacade } from '@kion/data-access/data-access-network-partners';
import { UsersFacade } from '@kion/data-access/data-access-users';
import { PrivilegesFacade } from '@kion/data-access/data-access-privileges';
import { SubSink } from 'subsink';

@Component({
  selector: 'kion-cms-nwp-list',
  templateUrl: './nwp-list.component.html',
  styleUrls: ['./nwp-list.component.scss']
})
export class NwpListComponent implements OnInit, OnDestroy {

  form: FormGroup;
  selectedRoles = [];
  networkPartners$: Observable<NetworkPartner[]>;
  privileges$: Observable<Privilege[]>;
  basestores$: Observable<{key: string, value: string, selected?: boolean}[]>;

  user$: Subject<User> = new Subject<User>();

  isSending = false;

  selectedValue = '';

  roles = [
    {
      id: 'fsNwpAdmin',
      name: 'NetworkPartner Admin',
      privilege: 'inviteFSNetworkPartnerAdmin'
    },
    {
      id: 'fsNwpViewer',
      name: 'NetworkPartner Viewer',
      privilege: 'inviteFSNetworkPartnerViewer'
    },
    {
      id: 'fsNwpBuyer',
      name: 'NetworkPartner Buyer',
      privilege: 'inviteFSNetworkPartnerBuyer'
    },
    {
      id: 'fsNwpApprover',
      name: 'NetworkPartner Approver',
      privilege: 'inviteFSNetworkPartnerApprover'
    }
  ];

  private readonly subSink: SubSink;

  constructor(
    formBuilder: FormBuilder,
    private privilegesFacade: PrivilegesFacade,
    private usersFacade: UsersFacade,
    private networkPartnersFacade: NetworkPartnersFacade,
    private correlationObserver: CorrelationObserverService,
    private toastMessageService: ToastService,
    private cmsConfig: CmsConfig,
    private translationService: TranslateService
  ) {
    this.subSink = new SubSink();
    this.form = formBuilder.group({
      email: new FormControl(),
      firstName: new FormControl(),
      lastName: new FormControl(),
      baseStore: new FormControl(),
    });
    this.networkPartners$ = this.networkPartnersFacade.all$;
    this.privileges$ = this.privilegesFacade.all$;
  }

  ngOnInit(): void {
    this.usersFacade.loadUserById();
    this.networkPartnersFacade.loadAll();
    this.subSink.sink = this.usersFacade.all$.pipe(takeUntil(this.user$)).subscribe(
      users => {
        if (users.length > 0) {
          const user = users[(users.length - 1)];
          if (user) {
            this.user$.next(user);
            this.privilegesFacade.loadAll(user.uid)
          }
        }
      }
    );
    this.subSink.sink = this.networkPartners$.subscribe((values) => {
      if(!!values.length){
        this.selectedValue = values[0].id;
      }
    });

    this.basestores$ = this.networkPartners$.pipe(map((networkPartners) => {
      return networkPartners.map((networkpartner) => {
        return { value: networkpartner.name.concat(' -- ',networkpartner.id), key: networkpartner.id, selected: false}
      })
    }),
    )
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  addRoles(role: string): void {
    const i = this.selectedRoles.indexOf(role);
    if (i !== -1) {
      const roles = [];
      this.selectedRoles.forEach(
        (r, index) => {
          if (index !== i) {
            roles.push(r);
          }
        }
      );
      this.selectedRoles = roles;
      return;
    }
    this.selectedRoles.push(role);
  }

  inviteUser(): void {
    this.isSending = true;
    const baseStoreId = this.form.get('baseStore').value;
    const invitation: Invitation = {
      emailAddress: this.form.get('email').value,
      firstName: this.form.get('firstName').value,
      invitationRoleMappings: {
        roleMappings: this.selectedRoles.map(
          (role: string) => {
            return {
              baseStore: baseStoreId,
              role: {
                uid: role
              }
            }
          }
        )
      },
      lastName: this.form.get('lastName').value,
      networkPartnerId: baseStoreId,
      networkPartnerName: baseStoreId
    };
    this.subSink.sink = this.correlationObserver.subscribeToAction(
      this.usersFacade.inviteUser(invitation),
      () => {
        this.isSending = false;
        this.toastMessageService.show(
          {
            text: this.translationService.instant('admin.toast.invitation.success'),
            type: 'success',
          }
        )
      },
      () => {
        this.isSending = false;
        this.toastMessageService.show(
          {
            text: this.translationService.instant('admin.toast.invitation.error'),
            type: 'error',
          }
        )
      }
    )
  }

  hasPrivlege$(privlege: string): Observable<Boolean> {
    return  this.privilegesFacade.all$.pipe(
      map(
        (privleges) => !!privleges.find(p => p.id === privlege)
      )
    )
  }
}
